import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "flex flex-wrap items-center justify-end gap-2" }
const _hoisted_3 = { class: "px-4 pt-4 pb-2" }
const _hoisted_4 = { class: "text-lg font-medium" }
const _hoisted_5 = { class: "text-gray-500" }
const _hoisted_6 = {
  key: 0,
  class: "px-4 py-10 text-center text-gray-500"
}
const _hoisted_7 = {
  key: 1,
  class: "overflow-x-auto"
}
const _hoisted_8 = { class: "w-full text-normal" }
const _hoisted_9 = { class: "border-y border-gray-200 bg-gray-50 text-gray-500" }
const _hoisted_10 = { class: "sticky left-0 z-10 bg-gray-50 px-4 py-2 text-left font-medium" }
const _hoisted_11 = { class: "text-gray-900" }
const _hoisted_12 = { class: "font-normal" }
const _hoisted_13 = { class: "sticky left-0 z-10 bg-white px-4 py-2 text-left font-medium whitespace-nowrap" }
const _hoisted_14 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_15 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}

import { ref } from 'vue';
import { format, parseISO } from 'date-fns';
import { Download } from 'lucide-vue-next';
import { useToast } from 'vue-toastification';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import { drugUnitValue, formatQuantity, formatSignedPercent, PERCENT_TONE_CLASSES, percentTone } from '@/composables/inventory/inventoryTable';
import { quantityMetricOf, REPORT_METRIC_IDS, type ReportMetricId } from '@/composables/inventory/inventoryReport';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineReportColumn, MedicineReportFarm } from '@/types';
import { downloadBlob } from '@/util/downloadFile';
import { buildInventoryReportWorkbook } from '@/util/inventoryReportWorkbook';


export default /*@__PURE__*/_defineComponent({
  __name: 'ReportResult',
  props: {
    farms: {}
  },
  emits: ["reset"],
  setup(__props: any) {

const props = __props;



const toast = useToast();
const isExporting = ref(false);

const t = (key: string) => i18n.global.t(`views.health.inventory.report.${key}`);

const metricLabel = (metric: ReportMetricId) => t(`metrics.${metric}`);

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function periodOf(farm: MedicineReportFarm): string {
    return t('period').replace('%START%', formatDate(farm.start_date)).replace('%END%', formatDate(farm.end_date));
}

function columnLabel(column: MedicineReportColumn): string {
    return `${column.drug_name} (${column.unit_name})`;
}

/** A quantity metric of a column, or null for a figure that was not counted. */
function quantityOf(column: MedicineReportColumn, metric: ReportMetricId): number | null {
    return metric === 'deviation' ? null : quantityMetricOf(column, metric);
}

async function downloadExcel() {
    isExporting.value = true;

    try {
        const blob = await buildInventoryReportWorkbook({
            farms: props.farms,
            metricLabels: Object.fromEntries(REPORT_METRIC_IDS.map((metric) => [metric, metricLabel(metric)])) as Record<ReportMetricId, string>,
            metricHeader: t('metricHeader'),
            periodLabel: periodOf,
            columnLabel,
        });

        downloadBlob(blob, `stock-statement_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    } catch {
        // Without this the workbook silently fails to appear and the click looks dead.
        toast.error(t('exportError'));
    } finally {
        isExporting.value = false;
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(Button), {
        variant: "outline",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('reset')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.report.startOver')), 1)
        ]),
        _: 1
      }),
      _createVNode(_unref(Button), {
        disabled: isExporting.value,
        onClick: downloadExcel
      }, {
        default: _withCtx(() => [
          (isExporting.value)
            ? (_openBlock(), _createBlock(Spinner, {
                key: 0,
                class: "size-4 animate-spin",
                "aria-hidden": "true"
              }))
            : (_openBlock(), _createBlock(_unref(Download), {
                key: 1,
                class: "size-4",
                "aria-hidden": "true"
              })),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('views.health.inventory.report.downloadExcel')), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.farms, (farm) => {
      return (_openBlock(), _createBlock(MainViewCard, {
        key: farm.farm_id,
        class: "overflow-hidden"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h2", _hoisted_4, _toDisplayString(farm.farm_name), 1),
            _createElementVNode("div", _hoisted_5, _toDisplayString(periodOf(farm)), 1)
          ]),
          (farm.columns.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.$t('views.health.inventory.report.noColumns')), 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("table", _hoisted_8, [
                  _createElementVNode("thead", null, [
                    _createElementVNode("tr", _hoisted_9, [
                      _createElementVNode("th", _hoisted_10, _toDisplayString(_ctx.$t('views.health.inventory.report.metricHeader')), 1),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(farm.columns, (column) => {
                        return (_openBlock(), _createElementBlock("th", {
                          key: _unref(drugUnitValue)(column),
                          class: "px-4 py-2 text-right font-medium"
                        }, [
                          _createElementVNode("div", _hoisted_11, _toDisplayString(column.drug_name), 1),
                          _createElementVNode("div", _hoisted_12, _toDisplayString(column.unit_name), 1)
                        ]))
                      }), 128))
                    ])
                  ]),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(REPORT_METRIC_IDS), (metric) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: metric,
                        class: "border-b border-gray-100 last:border-b-0"
                      }, [
                        _createElementVNode("th", _hoisted_13, _toDisplayString(metricLabel(metric)), 1),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(farm.columns, (column) => {
                          return (_openBlock(), _createElementBlock("td", {
                            key: _unref(drugUnitValue)(column),
                            class: "px-4 py-2 text-right whitespace-nowrap"
                          }, [
                            (metric === 'deviation')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  (column.deviation_percent !== null)
                                    ? (_openBlock(), _createBlock(_unref(Badge), {
                                        key: 0,
                                        variant: "outline",
                                        class: _normalizeClass(_unref(PERCENT_TONE_CLASSES)[_unref(percentTone)(Math.abs(column.deviation_percent))])
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(_unref(formatSignedPercent)(column.deviation_percent)), 1)
                                        ]),
                                        _: 2
                                      }, 1032, ["class"]))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_14, "-"))
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  (quantityOf(column, metric) !== null)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: _normalizeClass({ 'font-semibold': metric === 'end' })
                                      }, _toDisplayString(_unref(formatQuantity)(quantityOf(column, metric)!)), 3))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_15, "-"))
                                ], 64))
                          ]))
                        }), 128))
                      ]))
                    }), 128))
                  ])
                ])
              ]))
        ]),
        _: 2
      }, 1024))
    }), 128))
  ]))
}
}

})